/**
 * Store Zustand pour gérer l'état du POS
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CartLine, Product, VendorRole, LotPromo } from '../types/pos.types';
import { logger } from '../utils/logger';
import { playBeep } from '../utils/sound';
import { apprendreSiFruitOuLegume } from '../services/camera/produceIndex';

/**
 * Custom hook pour accéder au panier du vendeur actuel avec réactivité
 * Utilise un sélecteur Zustand pour s'abonner uniquement au panier du vendeur actuel
 */
export function useVendorCart(): CartLine[] {
  return usePOSStore((state) => {
    const vendorId = state.vendorId;
    if (!vendorId) return [];
    return state.carts[vendorId] || [];
  });
}

// Codes des workstations standardisés (alignés avec le backend)
export const WORKSTATION_CODES = {
  POS: 'POS',           // Caisse - Point de vente (onglet 'pos')
  PREPARATION: 'PREPARATION', // Préparation commandes (onglet 'preparation')
  LIVE: 'LIVE',         // Live Shopping (onglet 'live')
  STOCKS: 'STOCKS',     // Gestion stock / Réception (backoffice)
  CHAT: 'CHAT',         // Messagerie clients (onglet 'chat')
  CLOSING: 'CLOSING',   // Clôture de caisse / Rapport Z
  FIELD: 'FIELD',       // Application terrain (tâches, récoltes)
  DELIVERY: 'DELIVERY', // Application livreur (tournées à domicile)
  DASHBOARD: 'DASHBOARD', // Tableau de bord administrateur
} as const;

export type WorkstationCode = typeof WORKSTATION_CODES[keyof typeof WORKSTATION_CODES];

interface POSStore {
  // Paniers par vendeur (isolés)
  carts: Record<string, CartLine[]>;

  // Tares actives par vendeur (en grammes), isolées comme le panier
  tares: Record<string, number>;

  // Helper pour obtenir le panier du vendeur actuel
  getCurrentVendorCart: () => CartLine[];

  addToCart: (
    product: Product,
    qty: number,
    unitPriceCents?: number,
    isReturn?: boolean,
    depositInfo?: { product: Product; qtyMultiplier?: number },
    /** Réponse à la question « quel lot ? » (remise anti-gaspi par lot). Absent = la poser si besoin. */
    choixLot?: ChoixLot,
  ) => void;
  removeFromCart: (index: number) => void;
  /** Annule le dernier encodage (l'article ET sa consigne éventuelle) */
  undoLastOperation: () => void;
  updateCartLineQty: (index: number, qty: number) => void;
  /**
   * Repeser une ligne pesée du panier : le panier pose la demande, POSContent la
   * route vers le flux de pesée habituel (balance, dialog), et la confirmation
   * REMPLACE la ligne au lieu d'en ajouter une. Transitoire — jamais persisté.
   */
  reweighRequest: { index: number; product: any } | null;
  requestReweigh: (index: number, product: any) => void;
  clearReweigh: () => void;
  /**
   * Garde-fou du panier laisse ouvert.
   *
   * Un panier encaisse au terminal mais NON cloture dans la caisse se fait
   * completer par le client suivant, qui repaye les articles du precedent.
   * Arrive le 08/08/2026 (ticket de 69,60 EUR a scinder) et le 19/09/2026
   * (3,76 EUR de pennes repayes). La caisse ne peut pas savoir que le terminal
   * a pris l'argent — le terminal n'est pas raccorde — donc on intervient au
   * seul moment qui trahit le probleme : l'ajout d'un article a un panier reste
   * en plan. Mesure prealable sur 455 enchainements : la mediane entre deux
   * ventes d'une meme caisse est de 6,8 min, et une minute d'inactivite est le
   * rythme NORMAL du comptoir — d'ou un seuil a 3 min et une question a la
   * reprise plutot qu'une minuterie qui crierait des dizaines de fois par jour.
   */
  lastCartActivityAt: Record<string, number>;
  /**
   * Ajouts suspendus en attente de la reponse « meme client ? ».
   * Une LISTE, pas un article : un ticket de pain ou une commande en ligne
   * ajoutent plusieurs articles d'affilee, et chacun bute sur la question.
   * Avec une seule place, chaque article ecrasait le precedent et seul le
   * DERNIER entrait au panier (« parfois un seul pain s'encode », 03/10/2026).
   */
  repriseEnAttente: {
    minutesEcoulees: number;
    ajouts: Array<[any, number, number | undefined, boolean, any, ChoixLot | undefined]>;
  } | null;
  confirmerReprise: () => void;
  annulerReprise: () => void;
  /**
   * Ajout suspendu : le produit a un lot REMISÉ en stock, la caisse demande
   * lequel le client a en main (fenêtre ChoixLotDialog). Voir addToCart.
   */
  choixLotEnAttente: QuestionLot | null;
  /**
   * Questions de lot qui attendent leur tour, derrière celle affichée. Plusieurs
   * articles remisés ajoutés d'affilée (une liste, une reprise de panier) : avec
   * une seule place, chaque question écrasait la précédente et l'article d'avant
   * n'entrait jamais — le défaut du ticket de pain (03/10/2026), même forme.
   */
  choixLotFile: QuestionLot[];
  choisirLot: (choix: ChoixLot) => void;
  annulerChoixLot: () => void;
  clearCart: () => void;
  loadCartFromSnapshot: (snapshot: { lines: CartLine[]; customerId?: string | null; preorderId?: string | null; activeTareGrams?: number; invoiceRequested?: boolean; ecomOrderId?: string | null; ecomOrderPaid?: boolean; ecomOrderDueCents?: number; ecomOrderPaidCents?: number }) => void;

  // Total du panier
  getCartTotal: () => number;

  // Vendeur actuel
  vendorId: string | null;
  setVendorId: (id: string | null) => void;
  currentVendorName: string | null;
  setCurrentVendorName: (name: string | null) => void;
  currentVendorRole: VendorRole | null;
  setCurrentVendorRole: (role: VendorRole | null) => void;

  // Workstations du vendeur actuel
  vendorWorkstations: string[];
  setVendorWorkstations: (codes: string[]) => void;

  // Vérifie si le vendeur a accès à une workstation spécifique
  hasWorkstationAccess: (code: WorkstationCode) => boolean;

  // Accès complet (Live Shopping, Préparation) - vérifié par PIN
  hasFullAccess: boolean;
  pinVerifiedAt: number | null;
  setFullAccess: (hasAccess: boolean) => void;

  // Vérifie si le vendeur actuel nécessite un PIN pour l'accès complet
  requiresPinForFullAccess: () => boolean;

  // Reset l'accès complet (à appeler lors du changement de vendeur)
  resetFullAccess: () => void;

  // Client actuel (pour paiement wallet)
  customerId: string | null;
  setCustomerId: (id: string | null) => void;
  customerWalletBalanceCents: number;
  setCustomerWalletBalanceCents: (cents: number) => void;

  // Demande de facture Enfact
  invoiceRequested: boolean;
  setInvoiceRequested: (requested: boolean) => void;

  // Verrou paiement en cours — bloque la déconnexion vendeur pour ne pas perdre le panier
  // pendant qu'un dialog Bancontact attend le retour du terminal
  paymentInProgress: boolean;
  setPaymentInProgress: (v: boolean) => void;

  // Suspend le scan EAN global du POS quand un dialog gère lui-même le scan
  // (ex. prélèvement/perte) — évite que le code-barres parte dans le panier en arrière-plan
  scanSuppressed: boolean;
  setScanSuppressed: (v: boolean) => void;

  // Pesée en cours (WeightConfirmDialog ouvert). Publié pour que RIEN ne vienne
  // s'ouvrir par-dessus la balance : « panier vide » ne veut pas dire « rien en
  // cours », le premier article d'un client se pèse justement panier vide.
  pesageEnCours: boolean;
  setPesageEnCours: (v: boolean) => void;

  // Onglet visible du poste de travail. Caisse et Préparation sont montées EN
  // MÊME TEMPS (l'onglet caché est seulement en display:none) et écoutaient
  // toutes deux la douchette : un scan en préparation partait AUSSI dans le
  // panier de la caisse (ricotta de la commande Mattei, 04/09/2026). C'est
  // l'onglet visible qui décide désormais qui reçoit le code-barres.
  activeWorkspaceTab: string;
  setActiveWorkspaceTab: (tab: string) => void;

  // Paiement FOLIES en cours de confirmation : montant et solde restant, poussés
  // vers l'ÉCRAN CLIENT. Au comptoir le client ne donne ni code ni signature —
  // il doit au moins VOIR ce qu'on s'apprête à prendre sur son compte, et
  // pouvoir dire non avant (04/09/2026).
  walletPreview: { amountCents: number; balanceAfterCents: number } | null;
  setWalletPreview: (p: { amountCents: number; balanceAfterCents: number } | null) => void;

  // Horodatage de la dernière sauvegarde réussie du panier (ms epoch)
  // Affiché dans l'UI pour donner au vendeur la confiance que tout est en sécurité
  cartLastSavedAt: number | null;
  setCartLastSavedAt: (ts: number | null) => void;

  // Précommande en cours de retrait
  preorderId: string | null;
  setPreorderId: (id: string | null) => void;

  // Commande e-commerce en cours d'encaissement (retrait au comptoir)
  ecomOrderId: string | null;
  ecomOrderPaid: boolean; // true si déjà payée en ligne (paiement 'online')
  ecomOrderDueCents: number; // complément de pesée à encaisser au retrait (poids supérieurs)
  /**
   * Total du panier À L'INSTANT où la commande y a été chargée. C'est LUI qui
   * borne ce qu'on peut réclamer au comptoir, et non ce que le client a payé :
   * la pesée de la préparation fait varier le total de quelques centimes, et la
   * règle est que ce surplus n'est JAMAIS facturé (décision du 04/09/2026, « le
   * maximum demandé au client est ce qu'il a déjà payé »). Seul ce qu'on AJOUTE
   * au comptoir dépasse ce repère et doit être encaissé.
   */
  ecomOrderBaseCents: number;
  // Ce qui a RÉELLEMENT été encaissé en ligne (orders.paid_cents). Sans lui, la caisse
  // déduisait le « déjà payé » par soustraction (total du panier − complément dû) : tout
  // article ajouté au comptoir gonflait donc le « déjà payé » et partait GRATUITEMENT,
  // en gonflant au passage la ligne « payé en ligne » du rapport Z.
  ecomOrderPaidCents: number;
  setEcomOrder: (
    id: string | null,
    paid?: boolean,
    dueCents?: number,
    paidCents?: number,
    baseCents?: number,
  ) => void;

  // Balance
  currentScaleWeight: number;
  scaleStatus: 'disconnected' | 'connected' | 'reading' | 'stable';
  isScaleConnected: boolean;
  lastScaleUpdate: number;
  activeTareGrams: number;

  // Actions balance
  setCurrentScaleWeight: (weight: number) => void;
  setScaleStatus: (status: string, connected: boolean) => void;
  getStableWeight: () => Promise<number>;
  setActiveTareGrams: (grams: number) => void;
  resetTare: () => void;

  // Caméra IA
  cameraConnected: boolean;
  setCameraConnected: (connected: boolean) => void;
  aiSuggestions: AISuggestion[];
  setAiSuggestions: (suggestions: AISuggestion[]) => void;
  clearAiSuggestions: () => void;
  isClassifying: boolean;
  setIsClassifying: (classifying: boolean) => void;

  // Produits POS (référence pour le matching local)
  posProducts: Product[];
  setPosProducts: (products: Product[]) => void;
}

export interface AISuggestion {
  name: string;
  category: string;
  confidence: number;
  variety?: string;
  description?: string;
  matchedProductId?: string;
  matchedProductName?: string;
  matchScore?: number;
  source?: 'local' | 'cloud';
}

/**
 * Delai au-dela duquel un panier non vide est considere « reste en plan », et
 * ou l'ajout d'un article pose la question « meme client ? ».
 *
 * 3 minutes par defaut : mesure du 19/09/2026 sur 455 enchainements de ventes
 * reelles — mediane de 6,8 min entre deux ventes d'une meme caisse, et seuls
 * 1,8 % des enchainements tiennent en moins d'une minute. Une minute aurait
 * donc interrompu le service normal (pesee, discussion, article oublie).
 * Reglable par caisse : localStorage `panier_seuil_reprise_min`.
 */
const SEUIL_REPRISE_MIN_DEFAUT = 3;

function seuilRepriseMs(): number {
  try {
    const v = Number(localStorage.getItem('panier_seuil_reprise_min'));
    if (Number.isFinite(v) && v > 0) return v * 60_000;
  } catch { /* stockage indisponible : on garde le defaut */ }
  return SEUIL_REPRISE_MIN_DEFAUT * 60_000;
}

/** Laisse passer l'ajout que l'operateur vient de confirmer. */

/** Question « quel lot ? » suspendue sur un article remisé. */
interface QuestionLot {
  args: [Product, number, number | undefined, boolean, any];
  promos: LotPromo[];
  normalExpiry: string | null;
  prixNormalCents: number;
}

/** Lot choisi au scan : un lot remisé, ou le prix normal. */
export type ChoixLot = { lot_id: string; percent: number; expiry_date: string | null } | 'normal';

/** Lots remisés encore valables aujourd'hui (la DLC d'hier ne se propose plus). */
function lotsRemisesValables(promos: LotPromo[] | undefined): LotPromo[] {
  if (!Array.isArray(promos) || promos.length === 0) return [];
  const aujourdhui = new Date().toISOString().slice(0, 10);
  return promos.filter((p) => !p.expiry_date || String(p.expiry_date).slice(0, 10) >= aujourdhui);
}

let repriseAutorisee = false;

/**
 * Mise en veille du garde-fou pour la JOURNEE, depuis la fenetre elle-meme.
 *
 * La porte de sortie prevue au depart etait une ligne a taper dans la console
 * de Safari : impraticable sur un ecran tactile en plein marche (signale par
 * Philippe le 19/09/2026). Elle est donc la ou l'on est quand la question
 * agace : dans la fenetre, en troisieme choix.
 *
 * Valable un JOUR, jamais indefiniment : une protection qu'on eteint en
 * s'ennuyant un samedi ne doit pas rester eteinte le mois suivant sans que
 * personne ne s'en souvienne.
 */
const CLE_VEILLE = 'panier_reprise_en_veille_le';

function jourCourant(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function mettreGardeFouEnVeille(): void {
  try {
    localStorage.setItem(CLE_VEILLE, jourCourant());
  } catch { /* stockage indisponible : tant pis, la question reviendra */ }
}

function gardeFouEnVeille(): boolean {
  try {
    return localStorage.getItem(CLE_VEILLE) === jourCourant();
  } catch {
    return false;
  }
}

export const usePOSStore = create<POSStore>()(
  persist(
    (set, get) => ({
  // État initial
  carts: {}, // Paniers isolés par vendorId
  tares: {}, // Tares actives par vendorId (grammes)

  // Helper pour obtenir le panier du vendeur actuel
  getCurrentVendorCart: () => {
    const state = get();
    const vendorId = state.vendorId;
    if (!vendorId) return [];
    return state.carts[vendorId] || [];
  },

  vendorId: null,
  currentVendorName: null,
  currentVendorRole: null,
  vendorWorkstations: [], // Codes des workstations assignées au vendor
  customerId: null,
  customerWalletBalanceCents: 0,
  invoiceRequested: false,
  paymentInProgress: false,
  scanSuppressed: false,
  pesageEnCours: false,
  activeWorkspaceTab: 'pos',
  walletPreview: null,
  cartLastSavedAt: null,
  preorderId: null,
  ecomOrderId: null,
  ecomOrderPaid: false,
  ecomOrderDueCents: 0,
  ecomOrderPaidCents: 0,
  ecomOrderBaseCents: 0,
  currentScaleWeight: 0,
  isScaleConnected: false,
  scaleStatus: 'disconnected',
  lastScaleUpdate: 0,
  activeTareGrams: 0,

  // Caméra IA
  cameraConnected: false,
  aiSuggestions: [],
  isClassifying: false,

  // Produits POS (pour matching local)
  posProducts: [],

  // Accès complet (Live Shopping, Préparation)
  hasFullAccess: false,
  pinVerifiedAt: null,

  // Actions
  addToCart: (product, qty, unitPriceCents, isReturn = false, depositInfo, choixLot) => {
    const prixNormal = unitPriceCents ?? product.prices?.[0]?.amount_cents ?? 0;
    const isDeposit = product.is_deposit || false;

    // ── Garde-fou du panier reste en plan ──────────────────────────────────
    // Point d'etranglement UNIQUE : dix endroits appellent addToCart, et il y
    // en aura d'autres. Poser le controle ici plutot que sur chaque appelant,
    // c'est le seul moyen qu'un chemin ajoute demain naisse protege.
    // La consigne AUTOMATIQUE d'un contenant est posee dans le meme geste que lui
    // (plus bas, sans repasser ici) : elle suit donc sa question. Une consigne
    // ajoutee SEULE (ligne consigne d'une commande en ligne, retour de vidanges)
    // passe par la question comme tout article : exemptee jusqu'au 05/10/2026,
    // elle entrait seule dans le panier du client precedent quand on repondait
    // « nouveau client » au chargement d'une commande (trouve par le filet ecran).
    // ⚠️ DEFAILLANCE OUVERTE, volontairement : ce controle est sur le chemin
    // critique de l'encaissement. Toute erreur inattendue ici doit laisser
    // passer l'article — un garde-fou qui empeche de vendre serait pire que
    // l'incident qu'il previent. D'ou le try/catch, et la lecture defensive de
    // `lastCartActivityAt` (absent des etats persistes avant cette version).
    if (!repriseAutorisee && !gardeFouEnVeille()) {
      try {
        const vid = get().vendorId;
        const panier = vid ? get().carts[vid] || [] : [];
        const dernier = vid ? (get().lastCartActivityAt || {})[vid] : undefined;
        if (vid && panier.length > 0 && dernier) {
          const ecoule = Date.now() - dernier;
          if (ecoule >= seuilRepriseMs()) {
            const deja = get().repriseEnAttente;
            set({
              repriseEnAttente: {
                minutesEcoulees: Math.round(ecoule / 60_000),
                ajouts: [...(deja?.ajouts ?? []), [product, qty, unitPriceCents, isReturn, depositInfo, choixLot]],
              },
            });
            return; // l'article n'entre PAS tant que la question n'est pas tranchee
          }
        }
      } catch (e) {
        logger.error('[usePOSStore] garde-fou panier ignore (on laisse passer):', e);
      }
    }

    // ── Remise anti-gaspi par LOT ──────────────────────────────────────────
    // Le produit a un lot remisé en stock : on DEMANDE quel lot le client a en
    // main (le caissier compare la DLC imprimée sur l'article). Posé ICI, au
    // point d'étranglement unique, pour que tous les chemins d'ajout (scan,
    // recherche, rayons, pesée, caméra) passent par la question — la leçon du
    // scan pesé, corrigé sur un chemin et oublié sur l'autre.
    // Les lots remisés se lisent dans la fiche reçue OU, à défaut, dans la
    // liste de la caisse : le scan en ligne renvoie une fiche qui ne les porte pas.
    if (choixLot === undefined && !isReturn && !isDeposit && product?.id) {
      const promos = lotsRemisesValables(
        product.lot_promos ?? get().posProducts.find((p) => p.id === product.id)?.lot_promos,
      );
      if (promos.length > 0) {
        const fiche = get().posProducts.find((p) => p.id === product.id);
        const question: QuestionLot = {
          args: [product, qty, unitPriceCents, isReturn, depositInfo],
          promos,
          normalExpiry: product.lot_normal_expiry_date ?? fiche?.lot_normal_expiry_date ?? null,
          prixNormalCents: prixNormal,
        };
        // Une question est déjà à l'écran : celle-ci attend son tour, elle ne
        // la remplace pas.
        if (get().choixLotEnAttente) set({ choixLotFile: [...(get().choixLotFile || []), question] });
        else set({ choixLotEnAttente: question });
        return; // l'article n'entre PAS tant que le lot n'est pas choisi
      }
    }
    const lotRemise = choixLot && choixLot !== 'normal' ? choixLot : null;
    const price = lotRemise ? Math.round((prixNormal * (100 - lotRemise.percent)) / 100) : prixNormal;

    // Bip de confirmation à chaque article encodé (comme un scanner), si un vendeur est actif
    if (get().vendorId) playBeep();

    // Apprentissage auto F&L : encoder un fruit/légume (rayon Fruits/Légumes, pesé OU à la
    // pièce) juste après une capture caméra apprend l'empreinte → produit (silencieux ;
    // no-op sans capture récente, ce qui exclut les produits scannés au code-barres).
    // La décision « fruit ou légume ? » attend que les rayons soient connus.
    if (!isReturn && !isDeposit && product?.id) {
      apprendreSiFruitOuLegume(product as any);
    }

    set((state) => {
      const vendorId = state.vendorId;
      if (!vendorId) {
        logger.error('[usePOSStore] addToCart: Aucun vendeur sélectionné');
        return state; // Pas de modification
      }

      // Récupérer le panier du vendeur actuel
      const vendorCart = state.carts[vendorId] || [];

      // CHRONOLOGIE : chaque encodage crée SA ligne, jamais de fusion avec une ligne
      // antérieure. Sans cela, un article scanné deux fois disparaissait dans la quantité
      // d'une ligne plus ancienne — impossible ensuite de défaire les derniers gestes.
      // Cas d'usage décisif (marché, août 2026) : un vendeur qui encode par erreur sur le
      // compte d'un collègue doit pouvoir retirer SES articles sans détruire le panier
      // de l'autre.
      const opId = vendorCart.reduce((max, l) => Math.max(max, l.op_id ?? 0), 0) + 1;

      let newVendorCart: CartLine[] = [
        ...vendorCart,
        {
          op_id: opId,
          product,
          qty,
          unit_price_cents: price,
          is_deposit: isDeposit,
          is_return: isReturn,
          ...(lotRemise ? {
            lot_id: lotRemise.lot_id,
            lot_promo_percent: lotRemise.percent,
            lot_expiry_date: lotRemise.expiry_date,
            original_price_cents: prixNormal,
          } : {}),
        },
      ];

      // Auto-ajout de la consigne associée (sauf en mode retour)
      if (depositInfo?.product && !isReturn) {
        const depositProduct = depositInfo.product;
        const depositPrice = depositProduct.prices?.[0]?.amount_cents ?? 0;
        const depositQty = qty * (depositInfo.qtyMultiplier ?? 1);

        // Même op_id que l'article : annuler l'encodage retire aussi sa consigne
        newVendorCart = [
          ...newVendorCart,
          {
            op_id: opId,
            product: depositProduct,
            qty: depositQty,
            unit_price_cents: depositPrice,
            is_deposit: true,
            is_return: false,
          },
        ];
      }

      // Mettre à jour le panier du vendeur dans l'objet carts
      return {
        carts: {
          ...state.carts,
          [vendorId]: newVendorCart,
        },
        lastCartActivityAt: { ...state.lastCartActivityAt, [vendorId]: Date.now() },
      };
    });
  },

  undoLastOperation: () => {
    set((state) => {
      const vendorId = state.vendorId;
      if (!vendorId) return state;
      const cart = state.carts[vendorId] || [];
      if (cart.length === 0) return state;

      const lastOp = cart.reduce((max, l) => Math.max(max, l.op_id ?? 0), 0);
      // Paniers d'avant l'horodatage des encodages : on retire simplement la dernière ligne
      const next = lastOp > 0
        ? cart.filter((l) => (l.op_id ?? 0) !== lastOp)
        : cart.slice(0, -1);

      return {
        carts: { ...state.carts, [vendorId]: next },
        lastCartActivityAt: { ...state.lastCartActivityAt, [vendorId]: Date.now() },
      };
    });
  },

  removeFromCart: (index) => {
    set((state) => {
      const vendorId = state.vendorId;
      if (!vendorId) return state;

      const vendorCart = state.carts[vendorId] || [];
      const newVendorCart = vendorCart.filter((_, i) => i !== index);

      return {
        carts: {
          ...state.carts,
          [vendorId]: newVendorCart,
        },
        lastCartActivityAt: { ...state.lastCartActivityAt, [vendorId]: Date.now() },
      };
    });
  },

  reweighRequest: null,
  requestReweigh: (index, product) => set({ reweighRequest: { index, product } }),
  clearReweigh: () => set({ reweighRequest: null }),
  lastCartActivityAt: {},
  repriseEnAttente: null,
  confirmerReprise: () => {
    const enAttente = get().repriseEnAttente;
    set({ repriseEnAttente: null });
    if (!enAttente) return;
    // Le drapeau est remis a false dans TOUS les cas : coince a true, le
    // garde-fou ne se declencherait plus jamais de la session.
    repriseAutorisee = true;
    try {
      // Dans l'ordre d'arrivee, chacun avec SON choix de lot (« normal » pour
      // le pain : sans lui la question du lot reviendrait apres la reprise).
      for (const ajout of enAttente.ajouts) get().addToCart(...ajout);
    } finally {
      repriseAutorisee = false;
    }
  },
  annulerReprise: () => set({ repriseEnAttente: null }),
  choixLotEnAttente: null,
  choixLotFile: [],
  choisirLot: (choix) => {
    const enAttente = get().choixLotEnAttente;
    // La suivante passe à l'écran AVANT d'ajouter : si l'ajout pose lui-même une
    // question (il ne devrait pas, le lot est choisi), elle se range derrière.
    const [suivante, ...reste] = get().choixLotFile || [];
    set({ choixLotEnAttente: suivante ?? null, choixLotFile: reste });
    if (!enAttente) return;
    // Même précaution que la reprise : la question « même client ? » a déjà
    // été posée (ou n'avait pas lieu d'être) avant celle du lot — ne pas la
    // reposer au moment où l'article entre enfin.
    repriseAutorisee = true;
    try {
      get().addToCart(...enAttente.args, choix);
    } finally {
      repriseAutorisee = false;
    }
  },
  // Renoncer à CET article seulement : la question suivante, s'il y en a, s'affiche.
  annulerChoixLot: () => {
    const [suivante, ...reste] = get().choixLotFile || [];
    set({ choixLotEnAttente: suivante ?? null, choixLotFile: reste });
  },

  updateCartLineQty: (index, qty) => {
    set((state) => {
      const vendorId = state.vendorId;
      if (!vendorId) return state;

      const vendorCart = state.carts[vendorId] || [];

      if (qty <= 0) {
        // Supprimer si quantité <= 0
        const newVendorCart = vendorCart.filter((_, i) => i !== index);
        return {
          carts: {
            ...state.carts,
            [vendorId]: newVendorCart,
          },
          lastCartActivityAt: { ...state.lastCartActivityAt, [vendorId]: Date.now() },
        };
      }

      const newVendorCart = [...vendorCart];
      newVendorCart[index].qty = qty;

      return {
        carts: {
          ...state.carts,
          [vendorId]: newVendorCart,
        },
        lastCartActivityAt: { ...state.lastCartActivityAt, [vendorId]: Date.now() },
      };
    });
  },

  clearCart: () => {
    set((state) => {
      const vendorId = state.vendorId;
      if (!vendorId) {
        // Si pas de vendeur, vider customerId, preorderId, ecomOrderId et invoiceRequested seulement
        return { customerId: null, preorderId: null, ecomOrderId: null, ecomOrderPaid: false, ecomOrderDueCents: 0, ecomOrderPaidCents: 0, ecomOrderBaseCents: 0, invoiceRequested: false };
      }

      // Vider le panier du vendeur actuel
      return {
        carts: {
          ...state.carts,
          [vendorId]: [],
        },
        lastCartActivityAt: { ...state.lastCartActivityAt, [vendorId]: Date.now() },
        customerId: null,
        preorderId: null,
        ecomOrderId: null,
        ecomOrderPaid: false,
        invoiceRequested: false,
      };
    });
  },

  loadCartFromSnapshot: (snapshot) => {
    set((state) => {
      const vendorId = state.vendorId;
      if (!vendorId) {
        logger.warn('[usePOSStore] loadCartFromSnapshot: Aucun vendeur sélectionné');
        return state;
      }

      // Tare présente dans le snapshot ? (peut être 0 explicite, donc on teste la présence)
      const snapshotTare = typeof (snapshot as any).activeTareGrams === 'number'
        ? (snapshot as any).activeTareGrams
        : 0;

      if (!snapshot?.lines || snapshot.lines.length === 0) {
        logger.log('[usePOSStore] Snapshot sans ligne — restaure client/facture si présents, vendeur:', vendorId);
        return {
          carts: {
            ...state.carts,
            [vendorId]: [],
          },
          lastCartActivityAt: { ...state.lastCartActivityAt, [vendorId]: Date.now() },
          tares: {
            ...state.tares,
            [vendorId]: snapshotTare,
          },
          activeTareGrams: snapshotTare,
          // Un panier peut n'avoir aucune ligne mais déjà un client / une demande de facture
          // (préparation d'une facture avant de scanner) — ne pas les écraser.
          customerId: (snapshot as any).customerId || null,
          preorderId: (snapshot as any).preorderId || null,
          invoiceRequested: !!(snapshot as any).invoiceRequested,
        };
      }

      logger.log(`[usePOSStore] Restauration de ${snapshot.lines.length} lignes depuis snapshot pour vendeur:`, vendorId, '— tare:', snapshotTare, 'g');

      // Le snapshot contient déjà les objets Product complets (Option A)
      // On restaure directement le panier du vendeur actuel
      return {
        carts: {
          ...state.carts,
          [vendorId]: snapshot.lines,
        },
        // Un panier RESTAURE repart a zero pour le garde-fou. L'horodatage est
        // local a la caisse, alors que le panier VOYAGE d'une caisse a l'autre :
        // au marche, changer de poste faisait lire l'heure du dernier passage
        // de ce vendeur SUR CETTE CAISSE — « derniere action il y a 13 minutes »
        // deux minutes apres un encodage fait a cote (signale par Philippe le
        // 20/09/2026, en plein marche). Ce que cette caisse sait, c'est que le
        // panier vient d'arriver : c'est de maintenant qu'on compte.
        lastCartActivityAt: { ...state.lastCartActivityAt, [vendorId]: Date.now() },
        tares: {
          ...state.tares,
          [vendorId]: snapshotTare,
        },
        activeTareGrams: snapshotTare,
        // Restaurer aussi customerId, preorderId et la demande de facture si présents
        customerId: (snapshot as any).customerId || null,
        preorderId: (snapshot as any).preorderId || null,
        invoiceRequested: !!(snapshot as any).invoiceRequested,
      };
    });

    logger.log('[usePOSStore] Panier restauré avec succès');
  },

  getCartTotal: () => {
    const state = get();
    const cart = state.getCurrentVendorCart();
    return cart.reduce((total, line) => {
      return total + line.unit_price_cents * line.qty;
    }, 0);
  },

  setVendorId: (id) => {
    logger.log('[usePOSStore] ⚡ setVendorId appelé:', { old: get().vendorId, new: id });
    // Synchroniser activeTareGrams avec la tare du nouveau vendeur (mémoire de session)
    const tares = get().tares;
    const tareForVendor = id ? (tares[id] ?? 0) : 0;
    set({ vendorId: id, activeTareGrams: tareForVendor });
    logger.log('[usePOSStore] ✅ vendorId mis à jour:', get().vendorId, '— tare synchronisée:', tareForVendor, 'g');
  },

  setCurrentVendorName: (name) => {
    logger.log('[usePOSStore] ⚡ setCurrentVendorName appelé:', { old: get().currentVendorName, new: name });
    set({ currentVendorName: name });
  },

  setCurrentVendorRole: (role) => {
    logger.log('[usePOSStore] ⚡ setCurrentVendorRole appelé:', { old: get().currentVendorRole, new: role });
    set({ currentVendorRole: role });
  },

  setVendorWorkstations: (codes) => {
    logger.log('[usePOSStore] ⚡ setVendorWorkstations appelé:', codes);
    set({ vendorWorkstations: codes });
  },

  hasWorkstationAccess: (code) => {
    const state = get();
    const role = state.currentVendorRole;

    // Les ADMIN et MANAGER ont accès à toutes les workstations
    if (role === 'ADMIN' || role === 'MANAGER') {
      return true;
    }

    // Pour les autres rôles, vérifier les workstations assignées
    return state.vendorWorkstations.includes(code);
  },

  setFullAccess: (hasAccess) => {
    logger.log('[usePOSStore] ⚡ setFullAccess appelé:', hasAccess);
    set({
      hasFullAccess: hasAccess,
      pinVerifiedAt: hasAccess ? Date.now() : null,
    });
  },

  requiresPinForFullAccess: () => {
    const state = get();
    const role = state.currentVendorRole;
    // CASHIER n'a jamais accès aux fonctionnalités étendues
    // ADMIN a accès sans PIN
    // MANAGER et CASHIER_PLUS nécessitent un PIN
    if (!role) return false;
    if (role === 'CASHIER') return false; // Pas d'accès complet possible
    if (role === 'ADMIN') return false;   // Accès direct sans PIN
    return true; // MANAGER et CASHIER_PLUS nécessitent PIN
  },

  resetFullAccess: () => {
    logger.log('[usePOSStore] ⚡ resetFullAccess appelé');
    set({
      hasFullAccess: false,
      pinVerifiedAt: null,
    });
  },

  setCustomerId: (id) => {
    set({ customerId: id });
    if (!id) set({ customerWalletBalanceCents: 0 });
  },

  setCustomerWalletBalanceCents: (cents) => {
    set({ customerWalletBalanceCents: cents });
  },

  setInvoiceRequested: (requested) => {
    set({ invoiceRequested: requested });
  },

  setPaymentInProgress: (v) => {
    set({ paymentInProgress: v });
  },

  setScanSuppressed: (v) => {
    set({ scanSuppressed: v });
  },

  setPesageEnCours: (v) => {
    set({ pesageEnCours: v });
  },

  setActiveWorkspaceTab: (tab) => {
    set({ activeWorkspaceTab: tab });
  },

  setWalletPreview: (p) => {
    set({ walletPreview: p });
  },

  setCartLastSavedAt: (ts) => {
    set({ cartLastSavedAt: ts });
  },

  setPreorderId: (id) => {
    set({ preorderId: id });
  },

  setEcomOrder: (id, paid = false, dueCents = 0, paidCents = 0, baseCents = 0) => {
    set({
      ecomOrderId: id,
      ecomOrderPaid: paid,
      ecomOrderDueCents: id ? Math.max(0, dueCents) : 0,
      ecomOrderPaidCents: id ? Math.max(0, paidCents) : 0,
      ecomOrderBaseCents: id ? Math.max(0, baseCents) : 0,
    });
  },

  setCurrentScaleWeight: (weight) => {
    set({
      currentScaleWeight: weight,
      lastScaleUpdate: Date.now()
    });
  },

  setScaleStatus: (status, connected) => {
    set({
      scaleStatus: status as any,
      isScaleConnected: connected
    });
  },

  setActiveTareGrams: (grams) => {
    const vendorId = get().vendorId;
    logger.log('[usePOSStore] Tare définie:', grams, 'g', vendorId ? `(vendeur: ${vendorId})` : '(sans vendeur)');
    if (!vendorId) {
      set({ activeTareGrams: grams });
      return;
    }
    set((state) => ({
      tares: { ...state.tares, [vendorId]: grams },
      activeTareGrams: grams,
    }));
  },

  resetTare: () => {
    const vendorId = get().vendorId;
    logger.log('[usePOSStore] Tare réinitialisée', vendorId ? `(vendeur: ${vendorId})` : '(sans vendeur)');
    if (!vendorId) {
      set({ activeTareGrams: 0 });
      return;
    }
    set((state) => ({
      tares: { ...state.tares, [vendorId]: 0 },
      activeTareGrams: 0,
    }));
  },

  // Caméra IA actions
  setCameraConnected: (connected) => {
    set({ cameraConnected: connected });
  },

  setAiSuggestions: (suggestions) => {
    set({ aiSuggestions: suggestions });
  },

  clearAiSuggestions: () => {
    set({ aiSuggestions: [] });
  },

  setIsClassifying: (classifying) => {
    set({ isClassifying: classifying });
  },

  setPosProducts: (products) => {
    set({ posProducts: products });
  },

  getStableWeight: () => {
    logger.log('[usePOSStore] 🔍 getStableWeight() called');
    const startState = get();
    logger.log('[usePOSStore] 📊 Initial state:', {
      scaleStatus: startState.scaleStatus,
      currentScaleWeight: startState.currentScaleWeight,
      isScaleConnected: startState.isScaleConnected
    });

    return new Promise((resolve) => {
      let checks = 0;
      const checkStable = setInterval(() => {
        const state = get();
        checks++;
        logger.log(`[usePOSStore] ⏱️ Check #${checks}:`, {
          scaleStatus: state.scaleStatus,
          currentScaleWeight: state.currentScaleWeight
        });

        if (state.scaleStatus === 'stable') {
          clearInterval(checkStable);
          logger.log('[usePOSStore] ✅ Weight is STABLE, resolving with:', state.currentScaleWeight, 'g');
          resolve(state.currentScaleWeight);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkStable);
        const finalWeight = get().currentScaleWeight;
        logger.log('[usePOSStore] ⏰ Timeout reached (3s), resolving with current weight:', finalWeight, 'g');
        resolve(finalWeight);
      }, 3000);
    });
  },
    }),
    {
      name: 'folies-pos-store',
      storage: createJSONStorage(() => localStorage),
      // Ne persister QUE l'identité vendeur + panier/tare/client → survie au reload, y
      // compris hors-ligne (incident marché 08/06/2026 : refresh hors-ligne vidait le panier).
      // PAS la balance, la caméra, le catalogue (posProducts), ni les états transitoires
      // (paiement en cours, suggestions IA, poids balance).
      partialize: (state) => ({
        carts: state.carts,
        // Conserve AVEC le panier, sinon le garde-fou du panier reste en plan
        // tombe precisement quand il sert le plus : la caisse rechargee (ou
        // rouverte le lendemain) restaure le panier mais aurait perdu l'heure
        // du dernier geste, donc ne poserait aucune question au scan suivant.
        lastCartActivityAt: state.lastCartActivityAt,
        tares: state.tares,
        vendorId: state.vendorId,
        currentVendorName: state.currentVendorName,
        currentVendorRole: state.currentVendorRole,
        vendorWorkstations: state.vendorWorkstations,
        hasFullAccess: state.hasFullAccess,
        pinVerifiedAt: state.pinVerifiedAt,
        customerId: state.customerId,
        preorderId: state.preorderId,
        activeTareGrams: state.activeTareGrams,
        ecomOrderId: state.ecomOrderId,
        ecomOrderPaid: state.ecomOrderPaid,
        // Sans ces deux-la, un rechargement de la PWA en pleine remise faisait
        // disparaitre le complement : du = 0, encaissement direct, difference perdue.
        ecomOrderDueCents: state.ecomOrderDueCents,
        ecomOrderPaidCents: state.ecomOrderPaidCents,
      }),
    },
  ),
);
